import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import {
  featuredArticles,
  howToUseArticle,
  bestCategoriesArticle,
  safetyReviewArticle,
  sneakersGuideArticle,
  streetwearTrendsArticle,
  budgetFindsArticle,
  qcPhotosGuideArticle,
  paymentSafetyArticle,
  accessoriesHaulArticle,
  sellerVerificationArticle,
  shippingTipsArticle,
  update2026Article,
} from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';
import Navbar from '@/pages/home/components/Navbar';

const SITE_URL = import.meta.env.VITE_SITE_URL;

const articleContentMap: Record<string, { title: string; date: string; readTime: string; category: string; content: string }> = {
  'how-to-use-vigorbuy-spreadsheet': howToUseArticle,
  'best-vigorbuy-spreadsheet-categories': bestCategoriesArticle,
  'is-vigorbuy-spreadsheet-safe': safetyReviewArticle,
  'vigorbuy-spreadsheet-sneakers-guide': sneakersGuideArticle,
  'vigorbuy-spreadsheet-streetwear-trends': streetwearTrendsArticle,
  'vigorbuy-spreadsheet-budget-finds': budgetFindsArticle,
  'vigorbuy-spreadsheet-qc-photos-guide': qcPhotosGuideArticle,
  'vigorbuy-spreadsheet-payment-safety': paymentSafetyArticle,
  'vigorbuy-spreadsheet-accessories-haul': accessoriesHaulArticle,
  'vigorbuy-spreadsheet-seller-verification': sellerVerificationArticle,
  'vigorbuy-spreadsheet-shipping-tips': shippingTipsArticle,
  'vigorbuy-spreadsheet-2026-update': update2026Article,
};

const defaultArticleContent = (article: typeof featuredArticles[0]) => ({
  title: article.title,
  date: article.date,
  readTime: article.readTime,
  category: article.category,
  content: `<h2>${article.title}</h2><p>${article.excerpt}</p><p>This comprehensive guide covers everything you need to know about the Vigorbuy Spreadsheet. As an independent educational resource, the <a href="/">Vigorbuy Spreadsheet</a> helps shoppers discover, compare, and find the best products across multiple categories. Whether you are exploring <a href="/#categories">Vigorbuy Spreadsheet categories</a> or learning about product verification, our <a href="/">resource center</a> has you covered.</p><h2>Why Vigorbuy Spreadsheet Matters</h2><p>The Vigorbuy Spreadsheet has transformed how people discover products online. By providing organized, curated product information with QC photos and detailed descriptions, it eliminates the guesswork from online shopping. Thousands of users rely on the <a href="/">Vigorbuy Spreadsheet</a> to make smarter purchasing decisions every day.</p><h2>Key Takeaways</h2><p>When using the Vigorbuy Spreadsheet, always review QC photos carefully, compare multiple options before purchasing, and verify seller information on the destination platform. For more detailed guidance, explore our full collection of <a href="/">Vigorbuy Spreadsheet articles and guides</a>.</p><p>Ready to start exploring? Visit the <a href="/">Vigorbuy Spreadsheet homepage</a> to browse all categories and discover your next great find.</p>`,
});

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => featuredArticles.find((a) => a.slug === slug), [slug]);
  const fullArticle = useMemo(
    () => (article ? (articleContentMap[slug!] || defaultArticleContent(article)) : null),
    [article, slug]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article || !fullArticle) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center pt-24 pb-20">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-accent-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = featuredArticles
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  return (
    <main>
      <Navbar />

      <article className="pt-24 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">Vigorbuy Spreadsheet Guides</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-8 md:mb-10">
            <span className="inline-block bg-secondary-100 text-secondary-800 text-xs font-medium px-2.5 py-1 rounded-full mb-4">
              {fullArticle.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
              {fullArticle.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {fullArticle.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {fullArticle.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> Vigorbuy Spreadsheet Team
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-8 md:mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - Vigorbuy Spreadsheet Resource`}
              className="w-full h-56 md:h-72 object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: fullArticle.content }}
          />

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t border-background-200">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
              Continue Reading Vigorbuy Spreadsheet Guides
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/blog/${ra.slug}`}
                  className="bg-background-100 rounded-lg overflow-hidden hover:bg-accent-50/30 transition-colors border border-background-200 cursor-pointer group"
                >
                  <img
                    src={ra.image}
                    alt={ra.title}
                    className="w-full h-32 object-cover object-top"
                  />
                  <div className="p-4">
                    <span className="text-xs text-accent-600 font-medium">{ra.category}</span>
                    <h4 className="text-sm font-semibold text-foreground-900 mt-1 leading-snug group-hover:text-accent-700 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 bg-accent-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> Vigorbuy Spreadsheet Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 bg-background-100 text-foreground-800 px-6 py-3 rounded-md text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap border border-background-200"
            >
              Visit Main Website <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${SITE_URL}/blog/${slug}`,
            "headline": fullArticle.title,
            "description": article.excerpt,
            "image": article.image,
            "url": `${SITE_URL}/blog/${slug}`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${SITE_URL}/blog/${slug}`
            },
            "datePublished": fullArticle.date,
            "dateModified": fullArticle.date,
            "author": {
              "@type": "Organization",
              "name": "Vigorbuy Spreadsheet Resource",
              "url": SITE_URL
            },
            "publisher": {
              "@type": "Organization",
              "name": "Vigorbuy Spreadsheet Resource",
              "url": SITE_URL,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg"
              }
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": SITE_URL
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Vigorbuy Spreadsheet Guides",
                "item": `${SITE_URL}/#guides`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": fullArticle.title
              }
            ]
          })
        }}
      />

      <Footer />
    </main>
  );
}